/// The /talktothedeveloper page. A list of posts (sortable Newest |
/// Top, paged with "Load more") with a collapsible "New post"
/// composer, and an in-page detail view when a post is opened. The
/// signed-in user (anonymous OK) comes from the profile store —
/// App.tsx only renders this screen once auth has resolved, so a
/// user is always available; the composer/voting just no-op if it
/// somehow isn't. Cookie consent is handled by the app-wide
/// <ConsentBanner /> that App.tsx renders alongside this page.

import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useProfileStore } from '../state/profileStore'
import {
  createPost,
  getMyVote,
  isFeedbackPost,
  registerPostContact,
  setVote,
  subscribePosts,
  voteAggregateDeltas,
  type DevPost,
  type SortKey,
  type VoteValue,
} from '../data/devBlog'
import { recordDevBlogVisit, type DevBlogVisitResult } from '../data/devBlogStats'
import { DevBlogComposer } from './DevBlogComposer'
import { DevBlogPostCard } from './DevBlogPostCard'
import { DevBlogPostDetail } from './DevBlogPostDetail'

const PAGE_SIZE = 25

type View = { kind: 'list' } | { kind: 'post'; postId: string }

export function DevBlogPage(props: { onHome: () => void; initialPostId?: string }) {
  const profileState = useProfileStore((s) => s.state)
  const user = profileState.kind === 'ready' ? profileState.user : null

  const [view, setView] = useState<View>(
    props.initialPostId ? { kind: 'post', postId: props.initialPostId } : { kind: 'list' },
  )
  const [sort, setSort] = useState<SortKey>('newest')
  const [feedbackOnly, setFeedbackOnly] = useState(false)
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [posts, setPosts] = useState<DevPost[] | null>(null) // null = loading
  const [err, setErr] = useState<string | null>(null)
  const [composerOpen, setComposerOpen] = useState(false)
  const [myVotes, setMyVotes] = useState<Record<string, VoteValue | null>>({})
  const [scoreAdj, setScoreAdj] = useState<Record<string, number>>({})
  const [stats, setStats] = useState<DevBlogVisitResult | null>(null)

  useEffect(() => {
    let cancelled = false
    void recordDevBlogVisit()
      .then((r) => {
        if (!cancelled) setStats(r)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (view.kind !== 'list') return
    setPosts(null)
    setErr(null)
    return subscribePosts({ sort, limit, onChange: setPosts, onError: (e) => setErr(e.message) })
  }, [view.kind, sort, limit])

  const postIdsKey = posts?.map((p) => p.id).join(',')
  useEffect(() => {
    if (!user || !posts) return
    let cancelled = false
    void Promise.all(
      posts.map(async (p) => [p.id, await getMyVote({ postId: p.id, voterUid: user.uid }).catch(() => null)] as const),
    ).then((pairs) => {
      if (cancelled) return
      setMyVotes((prev) => {
        const next = { ...prev }
        for (const [id, v] of pairs) next[id] = v
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [user?.uid, postIdsKey])

  const votePost = async (postId: string, next: VoteValue | null) => {
    if (!user) return
    const prev = myVotes[postId] ?? null
    const d = voteAggregateDeltas(prev, next)
    setMyVotes((m) => ({ ...m, [postId]: next }))
    setScoreAdj((m) => ({ ...m, [postId]: (m[postId] ?? 0) + d.score }))
    try {
      await setVote({ postId, voterUid: user.uid, value: next })
    } catch {
      setMyVotes((m) => ({ ...m, [postId]: prev }))
      setScoreAdj((m) => ({ ...m, [postId]: (m[postId] ?? 0) - d.score }))
    }
  }

  if (view.kind === 'post' && user) {
    return (
      <DevBlogShell onHome={props.onHome}>
        <DevBlogPostDetail postId={view.postId} user={user} onBack={() => setView({ kind: 'list' })} />
      </DevBlogShell>
    )
  }

  return (
    <DevBlogShell onHome={props.onHome}>
      <p className="text-sm leading-relaxed text-slate-400">
        Ask a question, leave feedback, or jump into someone else's thread. Posts and replies from the developer are
        <span className="mx-1 inline-block rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300 ring-1 ring-emerald-400/30">
          ✦ Developer
        </span>
        highlighted in green. Vote anything up or down; the net score can go negative. You can edit and delete your own
        posts and replies.
        {user?.isAnonymous && " You’re posting as a guest — sign in from the home screen if you want them tied to an account."}
      </p>

      {stats && (
        <p className="text-[11px] uppercase tracking-wider text-slate-500">
          {stats.viewCount.toLocaleString()} {stats.viewCount === 1 ? 'view' : 'views'}
          <span className="mx-1.5 text-slate-700">·</span>
          {stats.uniqueVisitorCount.toLocaleString()}{' '}
          {stats.uniqueVisitorCount === 1 ? 'unique visitor' : 'unique visitors'}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setComposerOpen((o) => !o)}
          disabled={!user}
          className={
            'rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white ' +
            (user ? 'hover:bg-emerald-500' : 'opacity-50')
          }
        >
          {composerOpen ? 'Cancel' : '✏️ New post'}
        </button>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => setFeedbackOnly((v) => !v)}
            className={
              'rounded px-2 py-1 font-medium ring-1 ' +
              (feedbackOnly
                ? 'bg-amber-400/20 text-amber-100 ring-amber-300/40'
                : 'text-slate-400 ring-white/10 hover:text-white')
            }
            title="Show only anonymous feedback posts"
          >
            ★ Feedback only
          </button>
          <div className="flex items-center gap-1">
            <span className="text-slate-500">Sort:</span>
            {(['newest', 'top'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                className={'rounded px-2 py-1 font-medium ' + (sort === s ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white')}
              >
                {s === 'newest' ? 'Newest' : 'Top'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {composerOpen && user && (
        <div className="rounded-lg bg-slate-900/40 p-3 ring-1 ring-white/5">
          <DevBlogComposer
            kind="post"
            submitLabel="Post"
            autoFocus
            notifyEmailField
            onCancel={() => setComposerOpen(false)}
            onSubmit={async ({ title, body, notifyEmail }) => {
              const id = await createPost({ author: user, title, body })
              if (notifyEmail) {
                // Best-effort: the post itself succeeded; a contact
                // failure should never eat it.
                try {
                  await registerPostContact({ postId: id, email: notifyEmail })
                } catch (e) {
                  console.warn('registerPostContact failed', e)
                }
              }
              setComposerOpen(false)
              setView({ kind: 'post', postId: id })
            }}
          />
        </div>
      )}

      {err && (
        <div className="rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">{err}</div>
      )}
      {posts === null && !err && <p className="text-sm text-slate-400">Loading…</p>}

      {posts && (() => {
        const renderCard = (p: DevPost) => (
          <DevBlogPostCard
            key={p.id}
            post={{ ...p, score: p.score + (scoreAdj[p.id] ?? 0) }}
            myVote={myVotes[p.id] ?? null}
            voteDisabled={!user || !!p.archivedAt}
            onVote={(next) => votePost(p.id, next)}
            onOpen={() => setView({ kind: 'post', postId: p.id })}
          />
        )

        // The "Recent feedback" strip is pinned regardless of the
        // user's sort choice — it always shows the newest unarchived
        // feedback posts so the developer's signal stream stays at
        // eye level. The cap is small (5) so it doesn't dominate the
        // page; everything else still reaches the main list below.
        // When "Feedback only" is on, the strip is suppressed (the
        // main list IS the feedback) and ordinary sort applies.
        const STRIP_CAP = 5
        const allFeedback = posts.filter((p) => !p.archivedAt && isFeedbackPost(p))
        const stripPosts = feedbackOnly ? [] : allFeedback.slice(0, STRIP_CAP)
        const stripIds = new Set(stripPosts.map((p) => p.id))

        const visible = feedbackOnly ? posts.filter((p) => isFeedbackPost(p)) : posts
        // Active and archived are split client-side: keeps the sort
        // (newest / top) the same within each group and gives us a
        // simple "Archived" section break at the boundary. If the
        // archived count ever grows large enough to push active posts
        // past the `limit` cap, switch to two queries with
        // `where('archivedAt', '==', null)` / `'!=' null` — for now
        // the board is small enough that this stays cheap.
        const active = visible.filter((p) => !p.archivedAt && !stripIds.has(p.id))
        const archived = visible.filter((p) => !!p.archivedAt)

        return (
          <>
            {stripPosts.length > 0 && (
              <section className="space-y-2 rounded-lg bg-amber-400/[0.04] p-3 ring-1 ring-amber-300/15">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-200/80">
                  <span>★ Recent feedback</span>
                  <span className="text-amber-200/40">·</span>
                  <span className="text-amber-200/60">anonymous</span>
                </div>
                <div className="space-y-2">{stripPosts.map(renderCard)}</div>
              </section>
            )}
            {active.length === 0 && stripPosts.length === 0 && (
              <p className="text-sm text-slate-500">
                {feedbackOnly ? 'No feedback yet.' : 'No posts yet — be the first to ask something.'}
              </p>
            )}
            <div className="space-y-2">{active.map(renderCard)}</div>
            {archived.length > 0 && (
              <>
                <div className="flex items-center gap-3 pt-2">
                  <div className="h-px flex-1 bg-slate-700/60" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Archived
                  </span>
                  <div className="h-px flex-1 bg-slate-700/60" />
                </div>
                <div className="space-y-2 opacity-70">{archived.map(renderCard)}</div>
              </>
            )}
          </>
        )
      })()}

      {posts && posts.length >= limit && (
        <div className="pt-1 text-center">
          <button
            type="button"
            onClick={() => setLimit((l) => l + PAGE_SIZE)}
            className="text-xs font-medium text-emerald-300 hover:underline"
          >
            Load more
          </button>
        </div>
      )}
    </DevBlogShell>
  )
}

function DevBlogShell(props: { onHome: () => void; children: ReactNode }) {
  return (
    <div className="h-full w-full overflow-y-auto bg-slate-950 text-slate-100">
      <div className="mx-auto w-full max-w-2xl px-4 pb-16 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-wide">Talk to the developer</h1>
          <button
            type="button"
            onClick={props.onHome}
            className="rounded-md bg-slate-900/70 px-2.5 py-1 text-xs font-semibold text-white/80 ring-1 ring-white/10 hover:bg-slate-800/80"
          >
            ← Home
          </button>
        </div>
        <div className="space-y-4">{props.children}</div>
      </div>
    </div>
  )
}
